import React, {
  useEffect, useState, useRef, useCallback,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { publicQueue } from "../../api";
import { AudioEngine } from "./AudioEngine";

const THEMES = {
  dark: {
    bg: "#060D1F", surface: "rgba(15,23,42,0.97)", border: "rgba(37,99,235,0.2)",
    accent: "#2563EB", ticketNum: "#FFFFFF", label: "#64748B", text: "#E2E8F0",
    sub: "#475569", headerBg: "#0A1023", banner: "#1E3A5F", bannerText: "#93C5FD",
    idle: "rgba(30,41,59,0.5)", panelBg: "#0D1628", panelHeader: "#111827",
    upNextHeader: "#1E1B4B", recentHeader: "#052E16", returningHeader: "#431407",
    stationStatus: "#0F172A", stationCard: "rgba(15,23,42,0.9)",
    serving: "#2563EB", available: "#16A34A",
    footerBg: "#1E3A5F", footerText: "#BFDBFE",
  },
  dark_blue: {
    bg: "#040C1C", surface: "rgba(10,20,50,0.97)", border: "rgba(59,130,246,0.25)",
    accent: "#3B82F6", ticketNum: "#FFFFFF", label: "#4B6CB7", text: "#E2E8F0",
    sub: "#4B6CB7", headerBg: "#05122E", banner: "#1E3A8A", bannerText: "#BFDBFE",
    idle: "rgba(15,30,70,0.5)", panelBg: "#071035", panelHeader: "#0A1845",
    upNextHeader: "#1E1B4B", recentHeader: "#052E16", returningHeader: "#431407",
    stationStatus: "#050E28", stationCard: "rgba(10,20,50,0.9)",
    serving: "#3B82F6", available: "#16A34A",
    footerBg: "#1E3A8A", footerText: "#BFDBFE",
  },
  light: {
    bg: "#EFF6FF", surface: "rgba(255,255,255,0.98)", border: "rgba(37,99,235,0.15)",
    accent: "#1D4ED8", ticketNum: "#0F172A", label: "#94A3B8", text: "#0F172A",
    sub: "#64748B", headerBg: "#FFFFFF", banner: "#1D4ED8", bannerText: "#FFFFFF",
    idle: "rgba(226,232,240,0.6)", panelBg: "#F1F5F9", panelHeader: "#E2E8F0",
    upNextHeader: "#312E81", recentHeader: "#14532D", returningHeader: "#7C2D12",
    stationStatus: "#E2E8F0", stationCard: "rgba(255,255,255,0.95)",
    serving: "#1D4ED8", available: "#16A34A",
    footerBg: "#1D4ED8", footerText: "#FFFFFF",
  },
};

const FONT_SIZES = { small: 0.82, normal: 1, large: 1.2, xlarge: 1.45 };
const STATION_COLORS = ["#3B82F6","#10B981","#F59E0B","#8B5CF6","#EF4444","#06B6D4","#EC4899","#84CC16"];

export default function DisplayBoardPage() {
  const { slug }          = useParams();
  const [searchParams]    = useSearchParams();
  const zoneFilter        = searchParams.get("zone");

  const [state,   setState]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const unlockedRef    = useRef(false);
  const [unlocked, setUnlocked] = useState(false);
  const prevServingRef = useRef({});
  const engineRef      = useRef(null);
  const pollRef        = useRef(null);
  const mountedRef     = useRef(true);

  // Station status pagination
  const [stationPage, setStationPage] = useState(0);
  const STATIONS_PER_PAGE = 5;

  // Up Next scroll
  const upNextScrollRef = useRef(null);
  const scrollAnimRef   = useRef(null);

  const poll = useCallback(async (since = null) => {
    if (!mountedRef.current) return;
    try {
      const params = {
        ...(since ? { since } : {}),
        ...(zoneFilter ? { zone: zoneFilter } : {}),
      };
      const { data } = await publicQueue.displayState(slug, params);
      if (!mountedRef.current) return;
      setState(data);
      setError(null);
      setLoading(false);

      if (data.settings || data.audio?.length) {
        if (!engineRef.current) {
          engineRef.current = new AudioEngine(data.audio || [], data.settings || {});
        } else {
          engineRef.current.setAssets(data.audio || []);
          engineRef.current.settings = data.settings || {};
          engineRef.current.volume   = (data.settings?.board_audio_volume ?? 80) / 100;
          engineRef.current.rate     = data.settings?.board_speech_rate ?? 1.0;
          engineRef.current.gender   = data.settings?.board_voice_gender || "default";
          engineRef.current.language = data.settings?.board_audio_language || "en";
        }
      }

      if (unlockedRef.current) {
        detectNewCalls(data, prevServingRef.current, engineRef.current, data.settings);
      }

      const nowServing = {};
      (data.zones || []).forEach(zone =>
        (zone.stations || []).forEach(station =>
          (station.counters || []).forEach(counter => {
            // Keyed on ticket_number + last_announced_at, not just
            // ticket_number — a staff "re-announce" click bumps
            // last_announced_at on the same ticket specifically so this
            // key changes and the chime/TTS replays, even though the
            // ticket being served hasn't actually changed.
            if (counter.serving) {
              nowServing[counter.id] = `${counter.serving.ticket_number}@${counter.serving.last_announced_at || ""}`;
            }
          })
        )
      );
      prevServingRef.current = nowServing;

      const ts = data.summary?.timestamp;
      pollRef.current = setTimeout(() => poll(ts), 200);
    } catch {
      if (!mountedRef.current) return;
      setLoading(false);
      setError("Connection lost — retrying…");
      pollRef.current = setTimeout(() => poll(null), 5000);
    }
  }, [slug, zoneFilter]);

  useEffect(() => {
    mountedRef.current = true;
    poll();
    return () => {
      mountedRef.current = false;
      clearTimeout(pollRef.current);
      window.speechSynthesis?.cancel();
    };
  }, [poll]);

  // Fullscreen management
  useEffect(() => {
    if (!state?.settings) return;
    const s = state.settings;
    if (!s.board_fullscreen) return;

    const tryFullscreen = () => {
      if (document.fullscreenElement) return;
      document.documentElement.requestFullscreen().catch(() => {});
    };

    // Auto re-enter if exited
    const onFsChange = () => {
      if (!document.fullscreenElement && s.board_auto_reenter_fullscreen) {
        setTimeout(tryFullscreen, 1000);
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, [state?.settings]);

  // Hide cursor
  useEffect(() => {
    if (!state?.settings) return;
    if (state.settings.board_hide_cursor) {
      document.body.style.cursor = "none";
    }
    return () => { document.body.style.cursor = ""; };
  }, [state?.settings]);

  // Compact overlay modes (ticker/side panel) render transparent, so the
  // rest of the browser viewport can be composited with a real TV feed
  // (OBS/vMix/hardware mixer, or a smart-TV split-screen input).
  useEffect(() => {
    const compact = (state?.settings?.board_layout || "full") !== "full";
    if (compact) {
      document.body.style.background = "transparent";
      document.documentElement.style.background = "transparent";
    }
    return () => {
      document.body.style.background = "";
      document.documentElement.style.background = "";
    };
  }, [state?.settings?.board_layout]);

  // Wake Lock (prevent screen sleep)
  useEffect(() => {
    if (!state?.settings?.board_prevent_sleep) return;
    let wakeLock = null;
    const acquire = async () => {
      try {
        wakeLock = await navigator.wakeLock?.request("screen");
      } catch {}
    };
    acquire();
    const reacquire = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", reacquire);
    return () => {
      document.removeEventListener("visibilitychange", reacquire);
      wakeLock?.release().catch(() => {});
    };
  }, [state?.settings]);

  // Heartbeat — ping server every 60s
  useEffect(() => {
    if (!state?.settings?.display_token && !slug) return;
    const token     = state?.settings?.display_token || slug;
    const zoneName  = zoneFilter ? (state?.zones?.[0]?.name || "Zone Board") : "Full Board";
    const ping = () => {
      fetch("/api/facilities/heartbeat/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          zone_id:   zoneFilter || "",
          zone_name: zoneName,
          label:     zoneName,
        }),
      }).catch(() => {});
    };
    ping();
    const interval = setInterval(ping, 60000);
    return () => clearInterval(interval);
  }, [slug, zoneFilter, state?.settings?.display_token]);

  // Auto-scroll Up Next
  useEffect(() => {
    const el = upNextScrollRef.current;
    if (!el) return;
    let pos = 0;
    const step = () => {
      if (!el) return;
      pos += 0.5;
      if (pos >= el.scrollHeight - el.clientHeight) pos = 0;
      el.scrollTop = pos;
      scrollAnimRef.current = requestAnimationFrame(step);
    };
    scrollAnimRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(scrollAnimRef.current);
  }, [state]);

  const handleUnlock = () => {
    unlockedRef.current = true;
    setUnlocked(true);
    // Enter fullscreen on first tap (required user gesture)
    if (settings.board_fullscreen !== false) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) new Ctx().resume();
    } catch {}
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      window.speechSynthesis.speak(u);
    }
  };

  const settings       = state?.settings || {};
  const layout         = settings.board_layout || "full";
  const theme          = THEMES[settings.board_theme] || THEMES.dark;
  const isDark         = (settings.board_theme || "dark") !== "light";
  const fontScale      = FONT_SIZES[settings.board_font_size] || 1;
  const cols           = Math.max(1, Math.min(8, settings.board_counters_per_row || 3));
  const summary        = state?.summary || {};
  const displayZones   = state?.zones || [];
  const recentlyCalled = state?.recently_called || [];

  // Flat lists
  const nowServing = [];
  const allStations = [];
  displayZones.forEach(z => {
    z.stations?.forEach(st => {
      allStations.push(st);
      st.counters?.forEach(c => {
        if (c.serving) nowServing.push({ ...c.serving, counterName: c.name, stationName: st.name });
      });
    });
  });

  const upNext = collectUpNext(displayZones);
  const allCountersAvailable = allStations.length > 0 && allStations.every(
    st => (st.counters || []).every(c => !c.serving && !c.is_away)
  );
  const totalStationPages = Math.ceil(allStations.length / STATIONS_PER_PAGE);
  const visibleStations = allStations.slice(
    stationPage * STATIONS_PER_PAGE,
    (stationPage + 1) * STATIONS_PER_PAGE
  );

  if (loading && !state) return <FullLoader />;
  if (error && !state) {
    return (
      <div style={{ ...S.fullScreen, background: "#060D1F" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "44px", marginBottom: "12px" }}>📡</div>
          <div style={{ color: "#EF4444", fontSize: "16px" }}>{error}</div>
        </div>
      </div>
    );
  }

  if (layout === "ticker_top" || layout === "ticker_bottom") {
    return (
      <TickerLayout
        position={layout === "ticker_top" ? "top" : "bottom"}
        theme={theme} settings={settings} summary={summary}
        nowServing={nowServing} upNext={upNext}
        unlocked={unlocked} onUnlock={handleUnlock} error={error}
      />
    );
  }

  if (layout === "side_left" || layout === "side_right") {
    return (
      <SidePanelLayout
        position={layout === "side_left" ? "left" : "right"}
        theme={theme} fontScale={fontScale} settings={settings} summary={summary}
        nowServing={nowServing} upNext={upNext} recentlyCalled={recentlyCalled}
        unlocked={unlocked} onUnlock={handleUnlock} error={error}
      />
    );
  }

  if (layout === "video_grid") {
    return (
      <VideoGridLayout
        theme={theme} fontScale={fontScale} settings={settings} summary={summary}
        allStations={allStations} nowServing={nowServing} upNext={upNext}
        unlocked={unlocked} onUnlock={handleUnlock} error={error}
      />
    );
  }

  return (
    <div style={{ ...S.root, background: theme.bg, fontSize: `${fontScale}rem`, position: "relative", overflow: "hidden" }}>
      <BoardWaves isDark={isDark} />

      {/* Audio unlock overlay */}
      {!unlocked && (
        <div style={{ ...S.unlockOverlay, zIndex: 20 }} onClick={handleUnlock}>
          <div style={S.unlockCard}>
            <div style={{ fontSize: "52px", marginBottom: "16px" }}>🔊</div>
            <div style={{ fontSize: "22px", fontWeight: "700", color: "#fff", marginBottom: "10px" }}>
              Tap to activate display board
            </div>
            <div style={{ fontSize: "14px", color: "#64748B", maxWidth: "320px", textAlign: "center", lineHeight: "1.7" }}>
              Browsers require a user interaction to enable audio.
            </div>
          </div>
        </div>
      )}

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <div style={{ ...S.header, background: theme.headerBg, borderBottom: `2px solid ${theme.border}`, position: "relative", zIndex: 1 }}>
        <div style={S.headerLeft}>
          <img
            src={`${process.env.PUBLIC_URL}/logo192.png`}
            alt="AuraQueue"
            style={{ width: "36px", height: "36px", borderRadius: "9px", objectFit: "cover", flexShrink: 0 }}
          />
          <div>
            <div style={{ fontSize: "1em", fontWeight: "700", color: theme.text, letterSpacing: "-0.3px" }}>Queue Display</div>
            {settings.facility_name && <div style={{ fontSize: "0.7em", color: theme.sub }}>{settings.facility_name}</div>}
          </div>
        </div>

        {settings.board_show_stats !== false && (
          <div style={S.statsRow}>
            <StatPill label="Waiting"    value={summary.total_waiting}   accent="#EAB308" theme={theme} />
            <StatPill label="Serving"    value={summary.total_serving}   accent={theme.accent} theme={theme} />
            <StatPill label="Done Today" value={summary.total_completed} accent="#22C55E" theme={theme} />
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <img
            src={`${process.env.PUBLIC_URL}/logo192.png`}
            alt="AuraQueue"
            style={{ height: "40px", width: "40px", borderRadius: "10px", objectFit: "cover", flexShrink: 0 }}
          />
          <LiveClock theme={theme} />
        </div>
      </div>

      {/* ── PADDED CONTENT ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "16px", padding: "16px", overflow: "hidden", minHeight: 0, position: "relative", zIndex: 1 }}>

      {/* ── ANNOUNCEMENT BANNER ────────────────────────────────────────────── */}
      {settings.board_announcement_active && settings.board_announcement && (
        <div style={{
          ...S.bannerBar,
          background: theme.banner,
          border: `1px solid ${theme.border}`,
          borderRadius: "16px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
          position: "relative", overflow: "hidden",
          flexShrink: 0,
        }}>
          <CardAura />
          <IconBadge icon="📢" bg="rgba(245,158,11,0.28)" />
          <div style={{ flex: 1, overflow: "hidden" }}>
            <div style={{ ...S.bannerText, color: theme.bannerText, display: "inline-block" }}>
              {settings.board_announcement}
            </div>
          </div>
        </div>
      )}

      {/* ── MAIN BODY ──────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", gap: "16px", overflow: "hidden", minHeight: 0 }}>

        {/* Left: Now Serving */}
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", overflow: "hidden",
          background: theme.panelBg, border: `1px solid ${theme.border}`, borderRadius: "18px",
          boxShadow: "0 8px 28px rgba(0,0,0,0.10)",
        }}>
          <div style={{ ...S.panelHeader, background: theme.accent, borderRadius: "18px 18px 0 0", position: "relative", overflow: "hidden" }}>
            <div style={S.headerShine} />
            <IconBadge icon="🔊" />
            <span style={S.panelHeaderText}>NOW SERVING</span>
          </div>
          {layout === "full_video" ? (
            <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
              <BackgroundVideo url={settings.board_background_video_url} mode="absolute" soundEnabled={settings.board_video_audio_enabled} unlocked={unlocked} />
              <div style={{
                position: "absolute", top: 0, left: 0, right: 0, zIndex: 2,
                display: "flex", alignItems: "center", gap: "14px", padding: "12px 20px",
                background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
              }}>
                {nowServing.length > 0 ? nowServing.slice(0, 3).map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span style={{
                      fontSize: "1.8em", fontWeight: "900",
                      color: s.priority === "emergency" ? "#F87171" : s.priority === "priority" ? "#FBBF24" : "#fff",
                      fontVariantNumeric: "tabular-nums", lineHeight: 1,
                    }}>{s.ticket_number}</span>
                    {s.priority !== "normal" && (
                      <span style={{
                        fontSize: "0.65em", fontWeight: "800", padding: "3px 8px", borderRadius: "6px",
                        background: s.priority === "emergency" ? "rgba(239,68,68,0.35)" : "rgba(245,158,11,0.35)",
                        color: s.priority === "emergency" ? "#FCA5A5" : "#FCD34D",
                      }}>
                        {s.priority === "emergency" ? "🚨 EMERGENCY" : "⚡ PRIORITY"}
                      </span>
                    )}
                    {s.is_appointment && (
                      <span style={{ fontSize: "0.65em", fontWeight: "800", padding: "3px 8px", borderRadius: "6px", background: "rgba(167,139,250,0.35)", color: "#D6BCFA" }}>
                        📅 APPOINTMENT
                      </span>
                    )}
                    <span style={{ fontSize: "0.8em", color: "#E2E8F0", fontWeight: "600" }}>{s.stationName} · {s.counterName}</span>
                  </div>
                )) : (
                  <span style={{ fontSize: "0.9em", color: "#CBD5E1" }}>No patients currently being served</span>
                )}
              </div>
              {!settings.board_background_video_url && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: theme.sub, fontSize: "0.8em", zIndex: 1 }}>
                  <CardAura opacity={0.4} />
                  <span style={{ position: "relative" }}>No background video configured — set one in Settings → Display Board.</span>
                </div>
              )}
            </div>
          ) : (
          <div style={{ flex: 1, overflowY: "auto", position: "relative" }}>
            <CardAura opacity={0.4} />
            {nowServing.length === 0 ? (
              <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "14px", padding: "24px", position: "relative" }}>
                <BenchIllustration color={theme.accent} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontWeight: "700", color: theme.text, fontSize: "1em" }}>No patients currently being served</div>
                  {allCountersAvailable && (
                    <div style={{ color: theme.sub, fontSize: "0.8em", marginTop: "4px" }}>All counters are currently available</div>
                  )}
                </div>
              </div>
            ) : nowServing.map((s, i) => (
              <div key={i} style={{ ...S.servingRow, borderBottom: `1px solid ${theme.border}`, background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)" }}>
                <div style={{
                  fontSize: `${fontScale * 2.8}em`,
                  fontWeight: "900",
                  color: s.priority === "emergency" ? "#EF4444" : s.priority === "priority" ? "#F59E0B" : theme.ticketNum,
                  letterSpacing: "-2px", lineHeight: 1,
                  fontVariantNumeric: "tabular-nums", minWidth: "120px",
                }}>
                  {s.ticket_number}
                </div>
                <div style={{ fontSize: "1.6em", color: theme.sub, fontWeight: "300" }}>→</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "1.1em", fontWeight: "700", color: theme.text }}>{s.stationName}</div>
                  <div style={{ fontSize: "0.8em", color: theme.accent, marginTop: "2px" }}>{s.counterName}</div>
                  {s.staff_name && settings.board_show_staff_names && (
                    <div style={{ fontSize: "0.7em", color: theme.sub, marginTop: "2px" }}>{s.staff_name}</div>
                  )}
                </div>
                {s.priority !== "normal" && (
                  <div style={{
                    fontSize: "0.65em", fontWeight: "700", padding: "4px 10px",
                    borderRadius: "6px",
                    background: s.priority === "emergency" ? "rgba(239,68,68,0.15)" : "rgba(245,158,11,0.15)",
                    color: s.priority === "emergency" ? "#EF4444" : "#F59E0B",
                  }}>
                    {s.priority === "emergency" ? "🚨 EMERGENCY" : "⚡ PRIORITY"}
                  </div>
                )}
                {s.is_appointment && (
                  <div style={{ fontSize: "0.65em", fontWeight: "700", padding: "4px 10px", borderRadius: "6px", background: "rgba(167,139,250,0.15)", color: "#A78BFA" }}>
                    📅 APPOINTMENT
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
        </div>

        {/* Right column 1: Up Next (top) + Recently Called (bottom) */}
        <div style={{
          width: "220px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "16px", overflow: "hidden",
        }}>

          {/* Up Next — scrolling */}
          <div style={{
            flex: 1, display: "flex", flexDirection: "column", overflow: "hidden",
            background: theme.panelBg, border: `1px solid ${theme.border}`, borderRadius: "16px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          }}>
            <div style={{ ...S.panelHeader, background: theme.upNextHeader, borderRadius: "16px 16px 0 0", position: "relative", overflow: "hidden" }}>
              <div style={S.headerShine} />
              <IconBadge icon="📋" size={26} />
              <span style={S.panelHeaderText}>UP NEXT</span>
            </div>
            <div
              ref={upNextScrollRef}
              style={{ flex: 1, overflowY: "hidden" }}
            >
              {settings.board_show_up_next !== false && upNext.length > 0 ? upNext.map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", borderBottom: `0.5px solid ${theme.border}` }}>
                  <span style={{ fontSize: "0.7em", color: theme.sub, minWidth: "16px", fontWeight: "700" }}>{i + 1}</span>
                  <span style={{ fontSize: "1em", fontWeight: "800", color: "#7C77E0", flex: 1, fontVariantNumeric: "tabular-nums" }}>{t.ticket_number}</span>
                  <span style={{ fontSize: "0.65em", color: theme.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "80px", textAlign: "right" }}>{t.station_name}</span>
                  {t.is_appointment && <span title="Appointment" style={{ fontSize: "0.75em", flexShrink: 0 }}>📅</span>}
                  {t.priority !== "normal" && <span style={{ width: "7px", height: "7px", borderRadius: "50%", flexShrink: 0, background: t.priority === "emergency" ? "#EF4444" : "#EAB308" }} />}
                </div>
              )) : (
                <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", padding: "16px" }}>
                  <IconBadge icon="👥" size={40} bg={`${theme.accent}18`} />
                  <div style={{ color: theme.sub, fontSize: "0.75em", textAlign: "center" }}>No patients queued</div>
                </div>
              )}
            </div>
          </div>

          {/* Recently Called */}
          <div style={{
            flex: 1, display: "flex", flexDirection: "column", overflow: "hidden",
            background: theme.panelBg, border: `1px solid ${theme.border}`, borderRadius: "16px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          }}>
            <div style={{ ...S.panelHeader, background: theme.recentHeader, borderRadius: "16px 16px 0 0", position: "relative", overflow: "hidden" }}>
              <div style={S.headerShine} />
              <IconBadge icon="🔔" size={26} />
              <span style={S.panelHeaderText}>RECENTLY CALLED</span>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {recentlyCalled.length === 0 ? (
                <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", padding: "16px" }}>
                  <IconBadge icon="🔔" size={40} bg="#10B98118" />
                  <div style={{ color: theme.sub, fontSize: "0.75em", textAlign: "center" }}>No calls yet today</div>
                </div>
              ) : recentlyCalled.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "9px 14px", borderBottom: `0.5px solid ${theme.border}` }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <span style={{ fontSize: "0.95em", fontWeight: "800", color: "#10B981", fontVariantNumeric: "tabular-nums" }}>{r.ticket_number}</span>
                      {r.is_appointment && <span title="Appointment" style={{ fontSize: "0.75em" }}>📅</span>}
                    </div>
                    <div style={{ fontSize: "0.65em", color: theme.text, marginTop: "1px" }}>{r.station_name}</div>
                    {r.counter_name && <div style={{ fontSize: "0.6em", color: theme.sub }}>{r.counter_name}</div>}
                  </div>
                  <div style={{ fontSize: "0.6em", color: theme.sub, flexShrink: 0 }}>{r.called_at}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* ── STATION STATUS ─────────────────────────────────────────────────── */}
      <div style={{
        background: theme.stationStatus, border: `1px solid ${theme.border}`, borderRadius: "18px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
        flexShrink: 0, overflow: "hidden",
      }}>
        {/* Station status header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 18px", borderBottom: `1px solid ${theme.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <IconBadge icon="🖥" size={24} bg={`${theme.accent}18`} />
            <span style={{ fontSize: "0.7em", fontWeight: "700", color: theme.label, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              Station Status
            </span>
          </div>
          {totalStationPages > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "0.6em", color: theme.sub }}>
                Page {stationPage + 1} of {totalStationPages}
              </span>
              <button
                onClick={() => setStationPage(p => Math.max(0, p - 1))}
                disabled={stationPage === 0}
                style={{ ...S.pageBtn, opacity: stationPage === 0 ? 0.3 : 1 }}
              >‹</button>
              <button
                onClick={() => setStationPage(p => Math.min(totalStationPages - 1, p + 1))}
                disabled={stationPage >= totalStationPages - 1}
                style={{ ...S.pageBtn, opacity: stationPage >= totalStationPages - 1 ? 0.3 : 1 }}
              >›</button>
            </div>
          )}
        </div>

        {/* Station cards */}
        <div style={{ display: "flex", gap: "12px", padding: "12px 18px", overflowX: "auto" }}>
          {visibleStations.map((st, i) => {
            const col = STATION_COLORS[(stationPage * STATIONS_PER_PAGE + i) % STATION_COLORS.length];
            return (
              <div key={st.id} style={{
                background: theme.stationCard,
                border: `1px solid ${col}33`,
                borderTop: `3px solid ${col}`,
                borderRadius: "12px",
                minWidth: "160px",
                flex: 1,
                overflow: "hidden",
              }}>
                {/* Station name header */}
                <div style={{ padding: "8px 12px", borderBottom: `1px solid ${col}22`, display: "flex", alignItems: "center", gap: "8px" }}>
                  <IconBadge icon="🏢" size={20} bg={`${col}22`} />
                  <div style={{ fontSize: "0.75em", fontWeight: "700", color: col, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {st.name} ({st.counters?.length || 0})
                  </div>
                </div>
                {/* Counters */}
                <div style={{ padding: "6px 12px 10px" }}>
                  {st.counters?.map(c => (
                    <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                      <span style={{ fontSize: "0.72em", color: theme.sub, fontWeight: "500" }}>{c.name}</span>
                      {c.is_away ? (
                        <span style={{ fontSize: "0.65em", color: "#D97706", fontWeight: "700", background: "rgba(217,119,6,0.12)", padding: "2px 7px", borderRadius: "4px" }}>Break</span>
                      ) : c.serving ? (
                        <span style={{ fontSize: "0.7em", fontWeight: "700", color: "#fff", background: theme.serving, padding: "2px 8px", borderRadius: "4px", fontVariantNumeric: "tabular-nums" }}>
                          {c.serving.is_appointment && "📅 "}{c.serving.ticket_number}
                        </span>
                      ) : (
                        <span style={{ fontSize: "0.65em", fontWeight: "700", color: theme.available }}>Available</span>
                      )}
                    </div>
                  ))}
                  {(!st.counters || st.counters.length === 0) && (
                    <div style={{ fontSize: "0.65em", color: theme.sub, fontStyle: "italic" }}>No counters</div>
                  )}
                </div>
              </div>
            );
          })}
          {allStations.length === 0 && (
            <div style={{ color: theme.sub, fontSize: "0.7em", padding: "8px 0" }}>No stations configured.</div>
          )}
        </div>
      </div>

      </div>
      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      <div style={{ ...S.footer, background: theme.footerBg, borderTop: `2px solid ${theme.border}`, position: "relative", zIndex: 1, overflow: "hidden" }}>
        <CardAura opacity={0.35} />
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, position: "relative" }}>
          {settings.branding_logo_url && (
            <img src={settings.branding_logo_url} alt="logo"
              style={{ height: "28px", objectFit: "contain", flexShrink: 0 }} />
          )}
          <span style={{ fontSize: "0.8em", fontWeight: "600", color: theme.footerText }}>
            {/* Falls back to the default courtesy line, not the announcement —
                that's already shown in its own banner above; repeating it
                here read as a duplicate. */}
            {settings.footer_message || "Please listen for your number and proceed to the assigned counter. Thank you!"}
          </span>
        </div>
        <div style={{ fontSize: "0.7em", fontWeight: "700", color: theme.footerText, opacity: 0.85, flexShrink: 0, position: "relative" }}>
          {settings.powered_by_text ? settings.powered_by_text : settings.board_show_powered_by !== false ? (
            <>♥ Powered by <span style={{ color: "#FCD34D", fontWeight: "800" }}>{settings.branding_name || "AuraQueue"}</span></>
          ) : null}
        </div>
      </div>

      {error && <div style={S.errorRibbon}>⚠ {error}</div>}
    </div>
  );
}

function StatPill({ label, value, accent, theme }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", background: `${accent}18`, border: `1px solid ${accent}33`, borderRadius: "10px", padding: "8px 16px", minWidth: "70px" }}>
      <div style={{ fontSize: "1.5em", fontWeight: "800", color: accent, lineHeight: 1 }}>{value ?? 0}</div>
      <div style={{ fontSize: "0.6em", color: theme.sub, fontWeight: "700", letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

function LiveClock({ theme }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const day = time.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const clk = time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <div style={{ textAlign: "right", flexShrink: 0 }}>
      <div style={{ fontSize: "1.8em", fontWeight: "800", color: theme.text, letterSpacing: "-0.5px", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{clk}</div>
      <div style={{ fontSize: "0.6em", color: theme.sub, marginTop: "2px" }}>{day}</div>
    </div>
  );
}

// Small circular icon badge used in panel headers/empty states instead of
// bare emoji, for the more polished "premium" look.
function IconBadge({ icon, size = 30, bg = "rgba(255,255,255,0.22)" }) {
  return (
    <span style={{
      width: `${size}px`, height: `${size}px`, borderRadius: "50%",
      background: bg, display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: `${size * 0.52}px`, flexShrink: 0,
    }}>
      {icon}
    </span>
  );
}

// Simple line-art waiting-bench illustration for the "no patients being
// served" empty state.
function BenchIllustration({ color = "#93C5FD", size = 84 }) {
  return (
    <svg width={size} height={size * 0.7} viewBox="0 0 120 84" fill="none">
      <rect x="14" y="30" width="92" height="14" rx="4" fill={color} opacity="0.35" />
      <circle cx="34" cy="20" r="13" fill={color} opacity="0.5" />
      <circle cx="60" cy="20" r="13" fill={color} opacity="0.5" />
      <circle cx="86" cy="20" r="13" fill={color} opacity="0.5" />
      <rect x="18" y="44" width="6" height="24" rx="2" fill={color} opacity="0.4" />
      <rect x="96" y="44" width="6" height="24" rx="2" fill={color} opacity="0.4" />
      <rect x="10" y="66" width="100" height="5" rx="2.5" fill={color} opacity="0.3" />
    </svg>
  );
}

// Faint decorative wave streaks behind the whole board — same visual
// language as the login page, toned down so it never competes with content.
// Wave-line + glowing "spark" accent for a single card — the BoardWaves
// background gets fully hidden behind each card's opaque background, so
// cards need their own copy of the effect to actually show it.
function CardAura({ color = "#F59E0B", opacity = 0.55 }) {
  return (
    <svg
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      viewBox="0 0 400 200" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={`auraLine-${color}`} x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity="0" />
          <stop offset="65%" stopColor={color} stopOpacity={opacity} />
          <stop offset="100%" stopColor="#FCD34D" stopOpacity={Math.min(opacity + 0.25, 1)} />
        </linearGradient>
        <radialGradient id={`auraSpark-${color}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
          <stop offset="35%" stopColor="#FCD34D" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#FCD34D" stopOpacity="0" />
        </radialGradient>
      </defs>
      <path d="M-20,175 C110,130 230,195 420,70" stroke={`url(#auraLine-${color})`} strokeWidth="2.5" fill="none" />
      <circle cx="345" cy="88" r="16" fill={`url(#auraSpark-${color})`} />
    </svg>
  );
}

function BoardWaves({ isDark }) {
  return (
    <svg
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }}
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="boardWaveBlue" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3B82F6" stopOpacity={isDark ? 0.22 : 0.12} />
          <stop offset="100%" stopColor="#2563EB" stopOpacity="0.02" />
        </linearGradient>
        <linearGradient id="boardWaveGold" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#F59E0B" stopOpacity={isDark ? 0.3 : 0.18} />
          <stop offset="100%" stopColor="#FCD34D" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d="M-100,700 C300,550 600,780 950,600 C1250,450 1400,650 1540,550" stroke="url(#boardWaveGold)" strokeWidth="3" fill="none" />
      <path d="M-100,100 C300,250 650,50 1000,180 C1250,270 1400,120 1540,200" stroke="url(#boardWaveGold)" strokeWidth="2" fill="none" />
      <path d="M-100,-50 C300,150 700,-100 1100,150 L1540,-50 L1540,-200 L-100,-200 Z" fill="url(#boardWaveBlue)" />
    </svg>
  );
}

// Extracts the video ID from any common YouTube URL shape
// (watch?v=, youtu.be/, embed/, with or without extra query params).
function extractYouTubeId(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1).split("/")[0] || null;
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      if (u.pathname.startsWith("/embed/")) return u.pathname.split("/embed/")[1]?.split("/")[0] || null;
      if (u.pathname.startsWith("/shorts/")) return u.pathname.split("/shorts/")[1]?.split("/")[0] || null;
    }
  } catch {}
  return null;
}

// ── Optional looping background video behind ticker/side-panel overlays ──────
// Lets a facility play their own promo/health-tips video in the area that
// would otherwise be blank/transparent, entirely within this page — no
// external hardware or mixer needed for this case. Accepts either a direct
// video file link (mp4 etc.) or a YouTube URL (auto-converted to an embed).
// mode="fixed" covers the whole viewport (ticker/side-panel overlays).
// mode="absolute" fills the nearest positioned ancestor instead — used to
// confine the video to a single panel (e.g. the Now Serving area in the
// Full Screen + Video layout) rather than the whole screen.
// soundEnabled/unlocked together gate whether the video actually plays
// audio. It always mounts muted (browsers won't autoplay with sound
// without a user gesture, and this board is meant to run unattended) —
// sound only turns on once someone taps the board's own "tap to
// activate" overlay (the same gesture used to unlock ticket-call TTS)
// AND the facility has explicitly opted in via Settings, since a promo
// video talking over a ticket-call announcement is something a facility
// should choose deliberately, not get by default.
function BackgroundVideo({ url, mode = "fixed", soundEnabled = false, unlocked = false }) {
  const videoRef = useRef(null);
  const allowSound = soundEnabled && unlocked;

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = !allowSound;
  }, [allowSound]);

  if (!url) return null;
  const posStyle = mode === "fixed"
    ? { position: "fixed", inset: 0 }
    : { position: "absolute", inset: 0 };
  const ytId = extractYouTubeId(url);
  if (ytId) {
    // YouTube's embed mute state can only be set at load time via the URL
    // param (no live prop to flip) — re-keying the iframe on allowSound
    // forces a fresh embed with the right mute state instead of a
    // permanently-muted one that can never actually unmute.
    const src = `https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&mute=${allowSound ? 0 : 1}&loop=1&playlist=${ytId}&controls=0&modestbranding=1&rel=0&playsinline=1`;
    return (
      <iframe
        key={allowSound ? "sound-on" : "sound-off"}
        src={src}
        title="Background video"
        allow="autoplay; encrypted-media"
        frameBorder="0"
        style={{ ...posStyle, width: "100%", height: "100%", zIndex: 1, pointerEvents: "none" }}
      />
    );
  }
  return (
    <video
      ref={videoRef}
      src={url}
      autoPlay muted loop playsInline
      style={{ ...posStyle, width: "100%", height: "100%", objectFit: "cover", zIndex: 1 }}
    />
  );
}

// ── Compact overlay: slim ticker bar (top or bottom) ─────────────────────────
// Meant to be composited alongside a live TV feed — everything outside the
// bar is transparent, so an operator's video mixer / smart-TV split-screen
// can show regular TV content in the rest of the screen. If a background
// video URL is configured, that plays behind the bar instead.
function TickerLayout({ position, theme, settings, summary, nowServing, upNext, unlocked, onUnlock, error }) {
  const current = nowServing[0];
  return (
    <>
      <BackgroundVideo url={settings.board_background_video_url} soundEnabled={settings.board_video_audio_enabled} unlocked={unlocked} />
      <div style={{ position: "fixed", left: 0, right: 0, [position]: 0, height: "110px", background: "transparent", fontFamily: "'DM Sans','Segoe UI',sans-serif", zIndex: 5 }}>
      {!unlocked && (
        <div style={S.unlockOverlay} onClick={onUnlock}>
          <div style={S.unlockCard}>
            <div style={{ fontSize: "40px", marginBottom: "10px" }}>🔊</div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#fff" }}>Tap to activate audio</div>
          </div>
        </div>
      )}
      <div style={{
        height: "100%", display: "flex", alignItems: "center", gap: "20px", padding: "0 24px",
        background: theme.headerBg,
        borderTop: position === "bottom" ? `2px solid ${theme.border}` : "none",
        borderBottom: position === "top" ? `2px solid ${theme.border}` : "none",
        boxShadow: "0 0 24px rgba(0,0,0,0.35)",
      }}>
        <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="AuraQueue" style={{ width: "48px", height: "48px", borderRadius: "10px", flexShrink: 0 }} />
        <div style={{ flexShrink: 0 }}>
          <div style={{ fontSize: "0.85em", fontWeight: "700", color: theme.text }}>{settings.facility_name || "Queue Display"}</div>
          <LiveClock theme={theme} />
        </div>
        <div style={{ width: "1px", height: "60%", background: theme.border, flexShrink: 0 }} />
        <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: "14px" }}>
          <span style={{ fontSize: "0.65em", fontWeight: "700", color: theme.label, textTransform: "uppercase", letterSpacing: "0.08em" }}>Now Serving</span>
          {current ? (
            <>
              <span style={{
                fontSize: "1.8em", fontWeight: "900", fontVariantNumeric: "tabular-nums",
                color: current.priority === "emergency" ? "#EF4444" : current.priority === "priority" ? "#F59E0B" : theme.ticketNum,
              }}>{current.ticket_number}</span>
              {current.priority !== "normal" && (
                <span style={{
                  fontSize: "0.6em", fontWeight: "800", padding: "3px 8px", borderRadius: "6px",
                  background: current.priority === "emergency" ? "rgba(239,68,68,0.18)" : "rgba(245,158,11,0.18)",
                  color: current.priority === "emergency" ? "#EF4444" : "#F59E0B",
                }}>
                  {current.priority === "emergency" ? "🚨 EMERGENCY" : "⚡ PRIORITY"}
                </span>
              )}
              {current.is_appointment && (
                <span style={{ fontSize: "0.6em", fontWeight: "800", padding: "3px 8px", borderRadius: "6px", background: "rgba(167,139,250,0.18)", color: "#A78BFA" }}>
                  📅 APPOINTMENT
                </span>
              )}
              <span style={{ fontSize: "0.85em", color: theme.accent, fontWeight: "700" }}>{current.stationName} · {current.counterName}</span>
            </>
          ) : <span style={{ fontSize: "0.85em", color: theme.sub }}>No patients currently being served</span>}
        </div>
        <div style={{ width: "1px", height: "60%", background: theme.border, flexShrink: 0 }} />
        <div style={{ flex: 1, overflow: "hidden", display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
          <span style={{ fontSize: "0.65em", fontWeight: "700", color: theme.label, textTransform: "uppercase", letterSpacing: "0.08em", flexShrink: 0 }}>Up Next</span>
          <div style={{ flex: 1, overflow: "hidden" }}>
            <div style={{
              fontSize: "0.85em", color: theme.text, fontWeight: "600",
              whiteSpace: "nowrap", display: "inline-block",
              animation: "eq-marquee 22s linear infinite",
            }}>
              {upNext.length > 0 ? upNext.map(t => t.ticket_number).join("   •   ") : "No patients queued"}
            </div>
          </div>
        </div>
        {settings.board_show_stats !== false && (
          <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
            <StatPill label="Waiting" value={summary.total_waiting} accent="#EAB308" theme={theme} />
            <StatPill label="Serving" value={summary.total_serving} accent={theme.accent} theme={theme} />
          </div>
        )}
      </div>
      {error && (
        <div style={{ ...S.errorRibbon, position: "absolute", left: 0, right: 0, [position === "top" ? "bottom" : "top"]: "-24px" }}>⚠ {error}</div>
      )}
      </div>
    </>
  );
}

// ── Compact overlay: side panel (left or right) ───────────────────────────────
function SidePanelLayout({ position, theme, fontScale, settings, summary, nowServing, upNext, recentlyCalled, unlocked, onUnlock, error }) {
  return (
    <>
      <BackgroundVideo url={settings.board_background_video_url} soundEnabled={settings.board_video_audio_enabled} unlocked={unlocked} />
      <div style={{
      position: "fixed", top: 0, bottom: 0, [position]: 0, width: "340px",
      background: "transparent", fontFamily: "'DM Sans','Segoe UI',sans-serif",
      fontSize: `${fontScale}rem`, zIndex: 5, display: "flex", flexDirection: "column",
    }}>
      {!unlocked && (
        <div style={S.unlockOverlay} onClick={onUnlock}>
          <div style={S.unlockCard}>
            <div style={{ fontSize: "40px", marginBottom: "10px" }}>🔊</div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#fff" }}>Tap to activate audio</div>
          </div>
        </div>
      )}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column", overflow: "hidden",
        background: theme.headerBg,
        borderLeft: position === "right" ? `2px solid ${theme.border}` : "none",
        borderRight: position === "left" ? `2px solid ${theme.border}` : "none",
        boxShadow: "0 0 24px rgba(0,0,0,0.35)",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "14px 16px", borderBottom: `2px solid ${theme.border}` }}>
          <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="AuraQueue" style={{ width: "34px", height: "34px", borderRadius: "8px", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "0.85em", fontWeight: "700", color: theme.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{settings.facility_name || "Queue Display"}</div>
            <div style={{ fontSize: "0.65em", color: theme.sub }}>Queue Display</div>
          </div>
        </div>

        {settings.board_show_stats !== false && (
          <div style={{ display: "flex", gap: "8px", padding: "10px 16px", borderBottom: `2px solid ${theme.border}` }}>
            <StatPill label="Waiting" value={summary.total_waiting} accent="#EAB308" theme={theme} />
            <StatPill label="Serving" value={summary.total_serving} accent={theme.accent} theme={theme} />
          </div>
        )}

        {/* Now Serving */}
        <div style={{ padding: "12px 16px", borderBottom: `2px solid ${theme.border}` }}>
          <div style={{ fontSize: "0.65em", fontWeight: "800", color: theme.label, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "8px" }}>Now Serving</div>
          {nowServing.length === 0 ? (
            <div style={{ fontSize: "0.75em", color: theme.sub }}>No patients currently being served</div>
          ) : nowServing.slice(0, 3).map((s, i) => (
            <div key={i} style={{ marginBottom: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{
                  fontSize: "1.4em", fontWeight: "900", fontVariantNumeric: "tabular-nums", lineHeight: 1,
                  color: s.priority === "emergency" ? "#EF4444" : s.priority === "priority" ? "#F59E0B" : theme.ticketNum,
                }}>{s.ticket_number}</div>
                {s.priority !== "normal" && (
                  <span style={{
                    fontSize: "0.55em", fontWeight: "800", padding: "2px 6px", borderRadius: "5px",
                    background: s.priority === "emergency" ? "rgba(239,68,68,0.18)" : "rgba(245,158,11,0.18)",
                    color: s.priority === "emergency" ? "#EF4444" : "#F59E0B",
                  }}>
                    {s.priority === "emergency" ? "🚨" : "⚡"}
                  </span>
                )}
                {s.is_appointment && <span title="Appointment" style={{ fontSize: "0.7em" }}>📅</span>}
              </div>
              <div style={{ fontSize: "0.7em", color: theme.accent, fontWeight: "700" }}>{s.stationName} · {s.counterName}</div>
            </div>
          ))}
        </div>

        {/* Up Next + Recently Called, stacked and scrollable */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 0" }}>
          <div style={{ padding: "0 16px 6px" }}>
            <div style={{ fontSize: "0.65em", fontWeight: "800", color: theme.label, letterSpacing: "0.1em", textTransform: "uppercase" }}>Up Next</div>
          </div>
          {upNext.length === 0 ? (
            <div style={{ padding: "0 16px 12px", fontSize: "0.7em", color: theme.sub }}>No patients queued</div>
          ) : upNext.slice(0, 6).map((t, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 16px" }}>
              <span style={{ fontSize: "0.9em", fontWeight: "800", color: "#7C77E0", fontVariantNumeric: "tabular-nums" }}>{t.ticket_number}</span>
              <span style={{ fontSize: "0.65em", color: theme.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.station_name}</span>
              {t.is_appointment && <span title="Appointment" style={{ fontSize: "0.7em", flexShrink: 0 }}>📅</span>}
            </div>
          ))}

          <div style={{ padding: "10px 16px 6px" }}>
            <div style={{ fontSize: "0.65em", fontWeight: "800", color: theme.label, letterSpacing: "0.1em", textTransform: "uppercase" }}>Recently Called</div>
          </div>
          {recentlyCalled.length === 0 ? (
            <div style={{ padding: "0 16px", fontSize: "0.7em", color: theme.sub }}>No calls yet today</div>
          ) : recentlyCalled.slice(0, 6).map((r, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 16px" }}>
              <span style={{ fontSize: "0.85em", fontWeight: "800", color: "#10B981", fontVariantNumeric: "tabular-nums" }}>{r.is_appointment && "📅 "}{r.ticket_number}</span>
              <span style={{ fontSize: "0.65em", color: theme.sub }}>{r.called_at}</span>
            </div>
          ))}
        </div>
      </div>
      {error && <div style={S.errorRibbon}>⚠ {error}</div>}
      </div>
    </>
  );
}

// ── Full-screen layout: counter-status grid + dominant video panel ──────────
// Not an overlay like Ticker/SidePanel above (those exist to composite over
// an external mixer's own video) — this is a standalone board where the
// video itself is the main content, sized to match a screen split roughly
// evenly between live queue status and signage. Requested layout: a
// branding header, a grid of one box per counter (ticket if serving,
// "Available" otherwise — so every counter is always visible, not just
// the ones currently serving), a large video panel, and a scrolling
// station ticker along the bottom.
function VideoGridLayout({ theme, fontScale, settings, summary, allStations, nowServing, upNext, unlocked, onUnlock, error }) {
  const allCounters = [];
  allStations.forEach(st => {
    (st.counters || []).forEach(c => allCounters.push({ ...c, stationName: st.name }));
  });
  const gridCounters = allCounters.slice(0, 6);

  const tickerItems = (nowServing.length > 0 ? nowServing : upNext).map(t =>
    `${t.stationName || t.station_name} · ${t.ticket_number}`
  );

  return (
    <div style={{ ...S.root, background: theme.bg, fontSize: `${fontScale}rem`, position: "relative", overflow: "hidden" }}>
      <BoardWaves isDark={(settings.board_theme || "dark") !== "light"} />

      {!unlocked && (
        <div style={{ ...S.unlockOverlay, zIndex: 20 }} onClick={onUnlock}>
          <div style={S.unlockCard}>
            <div style={{ fontSize: "52px", marginBottom: "16px" }}>🔊</div>
            <div style={{ fontSize: "22px", fontWeight: "700", color: "#fff", marginBottom: "10px" }}>
              Tap to activate display board
            </div>
            <div style={{ fontSize: "14px", color: "#64748B", maxWidth: "320px", textAlign: "center", lineHeight: "1.7" }}>
              Browsers require a user interaction to enable audio.
            </div>
          </div>
        </div>
      )}

      {/* ── BRANDING HEADER ─────────────────────────────────────────────── */}
      <div style={{ ...S.header, background: theme.headerBg, borderBottom: `2px solid ${theme.border}`, position: "relative", zIndex: 1 }}>
        <div style={S.headerLeft}>
          <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="AuraQueue" style={{ width: "36px", height: "36px", borderRadius: "9px", objectFit: "cover", flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: "1em", fontWeight: "700", color: theme.text, letterSpacing: "-0.3px" }}>{settings.facility_name || "Queue Display"}</div>
            <div style={{ fontSize: "0.7em", color: theme.sub }}>Queue Display</div>
          </div>
        </div>
        {settings.board_show_stats !== false && (
          <div style={S.statsRow}>
            <StatPill label="Waiting" value={summary.total_waiting} accent="#EAB308" theme={theme} />
            <StatPill label="Serving" value={summary.total_serving} accent={theme.accent} theme={theme} />
          </div>
        )}
        <LiveClock theme={theme} />
      </div>

      {/* ── MAIN BODY: counter grid (left) + video (right) ─────────────────── */}
      <div style={{ flex: 1, display: "flex", gap: "16px", padding: "16px", overflow: "hidden", minHeight: 0, position: "relative", zIndex: 1 }}>
        <div style={{
          width: "38%", minWidth: "260px", display: "grid",
          gridTemplateColumns: "1fr 1fr", gridAutoRows: "1fr", gap: "12px",
        }}>
          {gridCounters.length === 0 ? (
            <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "center", color: theme.sub, fontSize: "0.8em" }}>
              No counters configured.
            </div>
          ) : gridCounters.map((c, i) => {
            const col = STATION_COLORS[i % STATION_COLORS.length];
            return (
              <div key={c.id} style={{
                background: theme.stationCard, border: `1px solid ${col}33`, borderTop: `3px solid ${col}`,
                borderRadius: "14px", padding: "10px 12px", display: "flex", flexDirection: "column",
                justifyContent: "center", overflow: "hidden",
              }}>
                <div style={{ fontSize: "0.62em", fontWeight: "700", color: col, textTransform: "uppercase", letterSpacing: "0.05em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.stationName} · {c.name}
                </div>
                {c.is_away ? (
                  <div style={{ fontSize: "0.7em", color: "#D97706", fontWeight: "700", marginTop: "4px" }}>On Break</div>
                ) : c.serving ? (
                  <div style={{ fontSize: "1.5em", fontWeight: "900", color: theme.ticketNum, fontVariantNumeric: "tabular-nums", marginTop: "2px" }}>
                    {c.serving.is_appointment && <span style={{ fontSize: "0.6em" }}>📅 </span>}{c.serving.ticket_number}
                  </div>
                ) : (
                  <div style={{ fontSize: "0.8em", fontWeight: "700", color: theme.available, marginTop: "4px" }}>Available</div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{
          flex: 1, position: "relative", overflow: "hidden", borderRadius: "18px",
          background: theme.panelBg, border: `1px solid ${theme.border}`, boxShadow: "0 8px 28px rgba(0,0,0,0.10)",
        }}>
          {settings.board_background_video_url ? (
            <BackgroundVideo url={settings.board_background_video_url} mode="absolute" soundEnabled={settings.board_video_audio_enabled} unlocked={unlocked} />
          ) : (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px" }}>
              <div style={{ fontSize: "40px" }}>🎬</div>
              <div style={{ fontSize: "0.8em", color: theme.sub }}>No promo video configured</div>
            </div>
          )}
        </div>
      </div>

      {/* ── BOTTOM: scrolling station ticker ───────────────────────────────── */}
      <div style={{
        flexShrink: 0, display: "flex", alignItems: "center", gap: "16px",
        padding: "0 20px", height: "70px", background: theme.headerBg,
        borderTop: `2px solid ${theme.border}`, position: "relative", zIndex: 1, overflow: "hidden",
      }}>
        <div style={{ flex: 1, overflow: "hidden" }}>
          <div style={{
            fontSize: "1em", fontWeight: "700", color: theme.text, whiteSpace: "nowrap", display: "inline-block",
            animation: "eq-marquee 26s linear infinite",
          }}>
            {tickerItems.length > 0 ? tickerItems.join("     •     ") : "No patients currently queued"}
          </div>
        </div>
        <div style={{ width: "1px", height: "60%", background: theme.border, flexShrink: 0 }} />
        <span style={{ fontSize: "0.7em", fontWeight: "800", color: theme.label, letterSpacing: "0.1em", textTransform: "uppercase", flexShrink: 0 }}>
          Station
        </span>
      </div>

      {error && <div style={S.errorRibbon}>⚠ {error}</div>}
    </div>
  );
}

function FullLoader() {
  return (
    <div style={{ ...S.fullScreen, background: "#060D1F", flexDirection: "column", gap: "16px" }}>
      <div style={{ width: "48px", height: "48px", border: "3px solid rgba(37,99,235,0.2)", borderTopColor: "#2563EB", borderRadius: "50%", animation: "eq-spin 0.7s linear infinite" }} />
      <div style={{ color: "#475569", fontSize: "16px" }}>Loading display board…</div>
    </div>
  );
}

function collectUpNext(zones) {
  const all = [];
  zones.forEach(zone =>
    zone.stations?.forEach(station =>
      station.up_next?.forEach(t => {
        all.push({ ...t, station_name: station.name });
      })
    )
  );
  return all.sort((a, b) => {
    const p = { emergency: 0, priority: 1, normal: 2 };
    if (p[a.priority] !== p[b.priority]) return p[a.priority] - p[b.priority];
    return new Date(a.issued_at) - new Date(b.issued_at);
  });
}

function detectNewCalls(data, prev, engine, settings) {
  if (!engine) return;
  const lang = settings?.board_audio_language || "en";
  (data.zones || []).forEach(zone =>
    (zone.stations || []).forEach(station =>
      (station.counters || []).forEach(counter => {
        if (!counter.serving) return;
        const key = `${counter.serving.ticket_number}@${counter.serving.last_announced_at || ""}`;
        if (prev[counter.id] === key) return;
        // Prefer the station's dedicated "spoken name" over its display
        // name — a station like "Customer care - NON" has that suffix
        // purely to disambiguate it from "...Insured" in admin screens,
        // not to be read aloud.
        engine.announce(counter.serving.ticket_number, station.announcement_name || station.name, lang, counter.serving.staff_name || "", counter.name || "", counter.serving.spoken_name || "");
      })
    )
  );
}

const S = {
  root: { minHeight: "100vh", height: "100vh", display: "flex", flexDirection: "column", fontFamily: "'DM Sans','Segoe UI',sans-serif", userSelect: "none", overflow: "hidden" },
  fullScreen: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans',sans-serif" },
  unlockOverlay: { position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.88)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  unlockCard: { background: "rgba(15,23,42,0.97)", border: "1px solid rgba(37,99,235,0.3)", borderRadius: "24px", padding: "48px 52px", display: "flex", flexDirection: "column", alignItems: "center", boxShadow: "0 30px 80px rgba(0,0,0,0.7)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 24px", flexShrink: 0, gap: "16px" },
  headerLeft: { display: "flex", alignItems: "center", gap: "12px" },
  statsRow: { display: "flex", gap: "10px" },
  bannerBar: { display: "flex", alignItems: "center", gap: "12px", padding: "10px 24px", height: "42px", boxSizing: "border-box", flexShrink: 0, overflow: "hidden" },
  bannerText: { fontSize: "0.9em", fontWeight: "600", whiteSpace: "nowrap", animation: "eq-marquee 22s linear infinite" },
  panelHeader: { display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", flexShrink: 0 },
  panelHeaderText: { fontSize: "0.65em", fontWeight: "800", color: "#fff", letterSpacing: "0.12em", textTransform: "uppercase" },
  headerShine: {
    position: "absolute", top: 0, left: "-20%", width: "50%", height: "250%",
    background: "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.16) 50%, transparent 70%)",
    transform: "rotate(8deg)", pointerEvents: "none",
  },
  servingRow: { display: "flex", alignItems: "center", gap: "18px", padding: "16px 24px" },
  footer: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 24px", flexShrink: 0, gap: "16px" },
  errorRibbon: { background: "rgba(239,68,68,0.15)", color: "#EF4444", fontSize: "0.75em", fontWeight: "600", padding: "6px 20px", textAlign: "center", flexShrink: 0 },
  pageBtn: { background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "4px", color: "#fff", width: "24px", height: "24px", cursor: "pointer", fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 },
};
